/* eslint-disable no-irregular-whitespace */
import styles from 'components/cases/common/Interview/Interview.module.scss'
import TitleFlex from 'components/cases/common/TitleFlex'
import { useEffect, useRef } from 'react'
import { gsap, Power4 } from 'gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
import InterviewContents from 'components/cases/common/Interview/InterviewContents'
import { ContentData } from 'components/cases/common/Interview/fragments/type'
gsap.registerPlugin(ScrollTrigger)

const sectionTitle: { title: string; sub_title: string } = {
  title: 'Interview',
  sub_title: 'インタビュー',
}

const contentData: ContentData[][] = [
  [
    {
      type: 'textBox',
      texts: [
        '日本を代表する総合化学メーカーである、株式会社カネカ。その主力商品であるアクリル系合成繊維「カネカロン」を軸としたビジネスを展開する「Performance Fibers Solutions Vehicle（以下、PFSV）」事業部は、近年DX推進を急速に進めており、Aitriaはそのパートナーとして2019年から共にプロジェクトを進めています。',
        '世界に名だたる大企業であるカネカは過去どのように道を切り拓き、そしてあらゆる局面での変化が加速している現在どのようにDXに向き合い、自己変革を起こそうとしているのでしょうか？',
        '同社の執行役員にしてPFSV事業部長を務められている丸山竜一さんに、Aitria代表の芝 陽一郎がお話を伺いました。',
      ],
      wide: true,
      bold: false,
    },
    {
      type: 'sectionTitle',
      text: 'いますぐアフリカへ。カネカの「現場主義」を象徴する伝説的エピソード',
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/kaneka/Interview_1.webp',
      alt: 'インタビュー写真',
    },
    {
      type: 'textBox',
      texts: [
        '―― 「カネカさん＝チャレンジ・カンパニー」と私は認識しています。そして、そのチャレンジ精神を象徴するエピソードが、PFSVのコア事業の一つとして頭髪装飾などを手掛ける「頭髪事業」のアフリカ展開ではないでしょうか。この記事の読者の方々のために、その経緯をご紹介いただけますか？',
      ],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      texts: [
        '1950年代にカネカロンという合成繊維が生み出され、1960年代に日本や欧米でウィッグが流行したことから「頭髪事業」が始まりました。その後ブームは沈静化したのですが、80年代に入ってアフリカ系アメリカ人の方々がたくさん頭髪商品を使用しているという話を営業マンが聞きつけたことから、黒人の髪の毛に近い製品の開発が始まりました。中でもブレードと呼ばれる三つ編みスタイル用の製品を開発したところ、ちょうどその髪型の流行が起こりよく売れるようになりました。',
        'そこで当時の営業部が異変に気が付きました。「いくらブームが起こっているとはいえ、米国市場規模に対して輸出販売量が大きすぎる」と。その背景を解明するべくニューヨークのマンハッタンにある卸業者を営業担当者が訪問した際に、たくさんのアフリカ人行商の方々がそこに出入りしているのを目の当たりにします。彼らは、頭髪商品やアクセサリーなどの小物を買い付けてアフリカに持って帰って販売しており、その買い付け先であるカネカロンのお客さんが、行商の荷物の預かりから、飛行機に持ち込む荷造りまで面倒を見てあげていました。その営業マンはその足でアフリカまで追いかけ、宝の山ともいえるアフリカのマーケットを発見したのです。',
      ],
    },
  ],
  [
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/kaneka/Interview_2.webp',
      alt: 'インタビュー写真',
    },
    {
      type: 'textBox',
      texts: [
        '―― 営業部の気づきやリサーチ力も素晴らしいですが、アメリカで話を聞いて持ち帰るわけではなく、営業マンがそのままアフリカへ飛んだことには本当に驚かされます。',
      ],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      texts: [
        'しかも、ニューヨークからそのままアフリカへ向かったという話ですからね。その伝説の営業マンは、私の上司でしたからよくその時のお話を伺いました。当時の事業部長に出張先から報告して「アフリカに勝機がありそうだから行きたい」と伝え、そのまま渡航の手筈を整え、その時の事業部長も「そのまま行け」と指示した訳ですから、その行動力と決断の速さには驚かされます。そして実際にアフリカへ着き、頭髪商品が飛ぶように売れているのを目の当たりにしたわけです。それをきっかけに、その年からアフリカへの直接輸出が始まることになりました。私のロールモデルの原点ですね。',
      ],
    },
    {
      type: 'textBox',
      texts: ['―― アメリカからアフリカに直接飛んだのは、まさにカネカさんのチャレンジ精神の表れだと思います。'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      texts: [
        'カネカの企業風土として「現場主義」が醸成されていたのが大きいと思います。さらには「全部自分たちでこなす」というカルチャーもありました。70年代、韓国に頭髪商品の生産拠点を移した頃から、営業マンが毎月2週間は現地に出張し、商社を介すことなくお客さんから直接オーダーをもらっていたそうです。営業が貿易実務もこなしながら輸出する直販体制ですね。アフリカ市場開拓も、これらの企業文化の延長線にあったわけです。',
        '実際、私も入社してしばらくは頭髪事業関連のデリバリー業務を担当していましたが、傍ら韓国への単独出張にも行かせていただきました。拙い韓国語で四苦八苦しながら、お客さんとのアポ取りや、事務所や工場への訪問は、電車とバスを乗り継いで行っていました。',
      ],
    },
  ],
  [
    {
      type: 'sectionTitle',
      text: 'コロナ禍をきっかけに急展開した、カネカのDX推進',
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/kaneka/Interview_3.webp',
      alt: 'インタビュー写真',
    },
    {
      type: 'textBox',
      texts: [
        '―― 現在Aitriaは丸山さん率いるカネカ「Performance Fibers Solutions Vehicle」事業部と共にDX推進をご一緒させてもらっていますが、DX推進を実行するきっかけを教えていただけますか？',
      ],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      texts: [
        '直接的なきっかけは、2020年に起こった新型コロナウイルスのパンデミックです。ご存知の通り、人と人の接触が避けられるようになったことで、「現場主義」の精神を強く持つ我々がお客様のところに伺えないという状況になってしまい、大きなピンチとなりました。この未曾有の事態に対応するため、デジタル推進を前倒しして実行することに決断いたしました。',
      ],
    },
    {
      type: 'textBox',
      texts: [
        '―― カネカさんの企業文化として「全部自分たちでこなす」カルチャーがあると伺いましたが、DX推進において外部とパートナーシップを組んだ理由について伺えますか？',
      ],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      texts: [
        '私が1995年から2001年にかけてアメリカに駐在していたという経験が大きく影響していると思います。当時インターネット黎明期に起こったIT革命を肌で感じていたほか、アメリカでの分業主義が理解できましたので、専門家に任せるべきポイントと、自分たちが決断を下すべきポイントの仕分けはある程度理解していました。',
        'それと同時にオープンイノベーションの経験も大きかったと思います。2010年、私が主導して「アフリカでの大規模宣伝」をテーマに、大手広告代理店さんとオープンイノベーションを行った際には、改めてアウトソーシングの価値やインパクトを強く感じました。',
      ],
    },
  ],
  [
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/kaneka/Interview_4.webp',
      alt: 'インタビュー写真',
    },
    {
      type: 'textBox',
      texts: ['―― AitriaとDX推進に取り組むことになった経緯を、改めて教えていただけますか？'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      texts: [
        'コロナ以前に遡ります。当時、私はカネカ社内のメンバーから「スマートフォン向けアプリを作りたい」と相談を受けていましたが、そのアプリ開発のパートナーがAitriaさんでした。結果的にそのプロジェクトはいくつかの理由から中止することになってしまい、担当者がその経緯をアイスリーさんへご説明に伺いました。そして私はその担当者から、芝さんが「カネカさんがやらないならうちがやります」とおっしゃったという報告を受けたのです。それを聞いて「そんなにおもしろい人がいるのなら是非会ってみたい」と思い、大阪からまさに飛んで会いに行かせてもらいました。それがはじまりですね。',
        'そして、実際に芝さんとお会いして「こんな世界がデジタルにはあるのか」と感銘を受け、驚愕させられました。アフリカでのビジネス経験やデジタルに関する知識からくる、独自の考え方やビジネスの構想に非常にインスパイアされたわけです。これはぜひ一緒に仕事をしたいと帰りの機中で考え、空港に着陸するや否や社内担当者に「何か芝さんと仕事をしたいから、何とか接点を見つけてくれ」と携帯電話で伝えたのをよく覚えています。結果、芝さんから「ワークショップから始めてみるのはいかがでしょうか？」とご提案をいただき、そこから今のプロジェクトへつながる道がひらけたわけです。',
      ],
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/kaneka/Interview_5.webp',
      alt: 'インタビュー写真',
    },
    {
      type: 'textBox',
      texts: [
        '―― そこから2019年9月に事業検討のためのDesignSprintのワークショップをさせていただき、御社から20名強の方々にご参加いただきました。参加されたみなさんのご感想はいかがでしたか？',
      ],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      texts: [
        '「目からうろこ」という言葉がありますけれども、まさにそれでした。“デジタルを通して自分達の業務を考えたときに、たくさんの可能性があることに気がつけた。” そんなポジティブな反応が生まれていました。やはり自分と畑違いの分野を真剣に勉強しているメンバーは多くありませんし、表面的なコラムやお話程度では頭の中を通り抜けていってしまいます。そのため、ワークショップをしていただく以前までは、デジタルについての知識が多少あったとしても、自分の事業に適用して考え直すことはできていませんでした。',
        'そしてもう一つワークショップの経験として大きかったのが、「時間軸の違い」に気がつけたことです。芝さんは「デジタルの世界は、1年があったら世界が変わっている」とおっしゃっていましたよね。正直、我々の頭髪事業など繊維業界はそこまでのスピード感がありませんでした。しかし、デジタルの発達がさらに目覚ましい時代において、自分たちのポジションや軸をどこに置くかを考えないといけない。そうした課題意識の共有にもワークショップが役立ったと思います。',
        'また、いくら外部とパートナーシップを組んでDXを推進し、ソフトや技術的な面でのご提案をいただいたとしても、それらを事業プラットフォームやその戦略に落とし込むのは自社メンバーの仕事です。私としては、芝さんのワークショップによって、そうした実践が行えるようにメンバーの訓練をしていただいたとも感じております。',
      ],
    },
  ],
  [
    { type: 'sectionTitle', text: '加速するビジネスのスピード感。企業が時代に取り残されないためにすべきこと' },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/kaneka/Interview_6.webp',
      alt: 'インタビュー写真',
    },
    {
      type: 'textBox',
      texts: [
        '―― 近年、市場環境の変化がさらに加速しており、デジタルに限らずあらゆる分野で事業変革スピードが求められるようになっています。丸山さんの事業部では、社内の意識改革をどのように進めていらっしゃるのでしょうか？',
      ],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      texts: [
        '我々の事業部では、従来からの考え方にとらわれない、事業環境変化を見据えた事業変革を目標として掲げており、まさに今の時代急務だと認識しています。そして、会社もそれを我々に期待しています。そのためにまず進めているのは、先ずは我々の意識を変え、行動を変えることです。そして、次に我々のお客さまをはじめとするステークホルダーにもそれらを求めています。',
        'というのも、これまでカネカは圧倒的なシェアを持っているマーケットリーダーでした。そのため、自分達の都合やペースで事業を展開することが可能だったわけです。しかし、競合が増え、さらに事業の変化が激しくなっている今、その力学はもはや通じないと考えた方がいい。そこでまず「これまでのペースでやっていたら、負けてしまう」という状況を理解してもらい、意識改革を促すようにしています。',
      ],
    },
    {
      type: 'textBox',
      texts: [
        '―― カネカさんに限らず、大企業の多くはなかなかスピード感を上げることができず悩んでいるのではないかと思います。丸山さんは企業全体としてスピードを上げていくには、どのような手法が効果的だと考えていますか？',
      ],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      texts: [
        'あくまで私なりの捉え方ですが、まず重要なのはトップ層のコミットメントでありインクルージョン、覚悟ではないでしょうか。これがなければ変革は起こせません。次にアライアンスです。社内と社外両方のアライアンスを順番にではなく同時並行的に回すこと。そして、それを俯瞰してプロジェクトの進捗を管理することです。',
      ],
    },
  ],
  [
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/kaneka/Interview_7.webp',
      alt: 'インタビュー写真',
    },
    {
      type: 'textBox',
      texts: [
        '―― トップのコミットメントの重要性については、我々もいろいろなクライアントとお付き合いする中で非常に強く感じるポイントです。同じような課題として、トップ層とミドル層の間にある意識のギャップについてはどう考えられていますか？',
      ],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      texts: [
        'ギャップは組織の永遠の課題ですよね。トップ経営層とミドル層の間はもちろん、ミドル層とメンバー層との指揮や伝達にも常にギャップはつきものです。どうにかワンチームで仕事をしようとしても、プロジェクトが多部門にまたがれば、それだけであらゆる力学が働き、同じスピードで仕事を進めることができなくなってしまいます。',
        'では、そうした課題をどうクリアすべきかと言いますと、一般論にはなりますが「ヴィジョンや目指す未来を共有した上で、細かいギャップを一つずつ話し合いで埋めていく」しかないと思います。「トップが作ったヴィジョンにリーダー層が共鳴し、その上で自分の目線で他部門やメンバーに働きかけ、相互に活性化を図る」という構造がなければ、リーダー層が何を言っても組織は円滑に動きませんから。',
      ],
    },
    {
      type: 'textBox',
      texts: [
        '―― 他部門との調整は、大企業がスピード感を上げるための代表的な障壁だと思います。その解決にはどのような手法が有効だと考えられますか？',
      ],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      texts: [
        '同じ目線を持つために、危機感の共有が必要だと思います。正直、毎度危機感を煽ってお願いするというのは気持ちのいいものではないですし、個人的にも好きなやり方ではありません。しかし、時と場合によってはそれを示さないといけません。',
        'ただ、やはり危機感だけでは疲弊してしまいますよね。そこで改めて重要になってくるのが、ヴィジョンと危機感をセットで未来を描くことだと思います。',
      ],
    },
  ],
  [
    { type: 'sectionTitle', text: '大企業が抱える「イノベーションのジレンマ」にどう向き合うか？' },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/kaneka/Interview_8.webp',
      alt: 'インタビュー写真',
    },
    {
      type: 'textBox',
      texts: ['―― DX推進以外の面で、Aitriaとの仕事を通して何か得られたと感じるものはありましたか？'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      texts: [
        '私個人の話となりますが、人間関係と同時に視野も広がり、ビジネスをもっと楽しめるようになりました。そもそも私が芝さんのファンになったのは、芝さんがアフリカでの事業を行いそこでの失敗経験がある上で、もう一度チャレンジしたいと考えられていることに大きな理由があり、同時に信頼感を感じます。',
        'というのも、我々はアフリカでのビジネスに精力を費やしていますが、他の人にはどうしても状況が理解されません。「遠いので現地に行くのは大変ですよね」「食事も苦労されますよね」みたいな反応ばかりされてしまいますが、我々としてはそんなところに力点は置いていないし本質的ではない。なので、どんどんビジネスについての話をしなくなってしまうのです。一方ではビジネスを実践する上で、先進国にはない障壁や不合理も多く、的確な情勢分析も必要です。',
        'ところが、芝さんとはそうした前段階を踏む必要がなく、スッとビジネスの話ができた。そして、同じ目線を持った方をご紹介したり紹介されたりすると、どんどん話が広がるし、自分自身の視野も広がる。「世界は広く、自分の考えはちっぽけで、見つけていないアイデアはまだまだたくさんある」と気がつくことができるのです。それは本当に楽しいことですし、その気持ちでビジネスを楽しめる領域に入っていけていると感じます。',
      ],
    },
    {
      type: 'textBox',
      texts: ['―― 社外との交流から得られるものは非常に大きいですよね。'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      texts: [
        '事業というものは、成果蓄積をしながらも、一方で新しいこともやらないと先行きが苦しくなってしまいます。しかし、大企業は現業での成果蓄積への時間や資源の集中が起こりやすく「片手間で、できる時にやろう」みたいなやり方では挑戦もうまくいきません。かといって、いきなり新しいことに人材を集中させるとハレーションが起こってしまう……。',
        'そうしたジレンマの中でも我々が新しいことに挑戦できているのは、やはり芝さんとのプロジェクトやコミュニケーションが支えになっている部分は非常に大きいと思います。',
      ],
    },
    {
      type: 'textBox',
      texts: [
        '――「現業にフォーカスして効率化・合理化を図れば図るほど、イノベーションが起こった際に大きく市場を持っていかれてしまう」と、クレイトン・クリステンセンは書籍『イノベーションのジレンマ』の中で語っています。そうしたリスクを避けるためには、現業の効率化と同時に、現業に伏線を張った新しいチャレンジをいかに行えるかが大事になってくる、と。経営層であるトップが、いかにこれを意識して実行できるかがこれからの時代において非常に重要だと感じます。',
      ],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      texts: [
        '我々もちょうど最近、まさにイノベーションのジレンマに向き合っており、ものづくりや新製品開発において見直しをせざるを得ない状況に陥っています。しかし、DX推進で培った経験や、獲得した手法がその解決に使えるという手応えを感じています。DXとは、デジタルという技術をベースにしている考え方ではありますが、従来の研究や製造分野においても十分応用できるものだと思います。',
      ],
    },
    {
      type: 'textBox',
      texts: [
        '―― デジタルというのはあくまで手段ですから、あらゆる分野に応用可能です。例えば、イーロン・マスクのテスラなんかは、デジタル活用で製造業をより飛躍させている代表格ですよね。',
      ],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      texts: [
        'デジタルをやっていて「普段の仕事と違う」と感じるのは、基本的な姿勢が非常にポジティブであるということです。普段、戦略や新商品に関するディスカッションでは「過去にやったことがあるかどうか」「過去にダメだったからNG」といった基準が持ち出されることが多く、ネガティブな議論や発言が多くなりがちです。しかし、デジタルの世界では前例がないのが当たり前なので、みんなが経験していないことをやるしかないし、上を目指すしかない。それを前提に議論するスタンスはすごく良いなと思います。',
        'これだけ変化が早く激しい時代ですから、今後は我々の事業においても、過剰に過去を憧憬したり、過去の延長線上でばかり物事を見たりするのでは、通用しなくなるのではないかと感じますね。',
      ],
    },
    {
      type: 'textBox',
      texts: [
        '―― アフリカの若者と同じですね。彼らは「明日は今日より良くなる」と考える姿勢で生きている。そして、これは今の日本が一番失っているものだと思います。',
      ],
      wide: false,
      bold: true,
    },
  ],
]

const tweenVars: gsap.TimelineVars = {
  y: 50,
  duration: 1,
  opacity: 0,
  ease: Power4.easeOut,
}

const Interview: React.FC = () => {
  const timeline = gsap.timeline()
  const wrapper = useRef<HTMLElement>()
  const image = useRef<HTMLImageElement>()
  const text_container = useRef<HTMLDivElement>()
  // const container = useRef<HTMLDivElement>()

  useEffect(() => {
    timeline.from(image.current, tweenVars, 0.2)
    timeline.from(text_container.current, tweenVars, 0.4)

    ScrollTrigger.create({
      animation: timeline,
      trigger: wrapper.current,
      start: 'top-=100 80%',
      toggleActions: 'play none none reverse',
    })
  }, [])

  return (
    <section className={styles.wrapper} ref={wrapper}>
      <div className={styles.content}>
        <TitleFlex sectionTitle={sectionTitle} />
        <div className={styles.article_container} ref={text_container}>
          <InterviewContents contentData={contentData} />
        </div>
      </div>
    </section>
  )
}

export default Interview
